import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, Clock, ArrowLeft, Loader2 } from 'lucide-react';
import { getAvailableSlots, checkAvailability } from '../firebase/availability';
import { useToast } from './Toast';
import { debounce } from '../utils/debounce';
import { getNextDays, formatDisplayDate, isToday, isPastDate, sortTimeSlots } from '../utils/dateHelpers';
import { handleKeyboardNavigation, announceToScreenReader } from '../utils/accessibility';

/**
 * Reusable Booking Calendar Component
 * Handles date and time slot selection for booking products.
 * 
 * @param {Object} props
 * @param {function} props.onSlotSelect - Called when a slot is selected: (date, time) => void
 * @param {function} props.onCancel - Called when cancellation is requested
 * @param {string} props.title - Optional title override
 * @param {string} props.subtitle - Optional subtitle override
 */
export function BookingCalendar({ onSlotSelect, onCancel, title, subtitle }) {
    const [step, setStep] = useState(1); // 1: Date, 2: Time
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [dateAvailability, setDateAvailability] = useState({});
    const [hoveredDate, setHoveredDate] = useState(null);

    const { showToast } = useToast();

    // Memoize available dates
    const availableDates = useMemo(() => getNextDays(30), []);

    // Debounced date availability check
    const checkDateHasSlotsDebounced = useCallback(
        debounce(async (date) => {
            const dateKey = date.toISOString().split('T')[0];
            if (dateAvailability[dateKey] !== undefined) {
                return dateAvailability[dateKey];
            }
            try {
                const slots = await getAvailableSlots(date);
                const hasSlots = slots.length > 0;
                setDateAvailability(prev => ({
                    ...prev,
                    [dateKey]: hasSlots
                }));
                return hasSlots;
            } catch {
                return true; // Optimistic
            }
        }, 300),
        [dateAvailability]
    );

    // Pre-check availability for visible dates on mount
    useEffect(() => {
        const preloadAvailability = async () => {
            const today = new Date();
            const nextWeek = new Date(today);
            nextWeek.setDate(today.getDate() + 7);

            const datesToCheck = availableDates.filter(d => {
                const dateKey = d.toISOString().split('T')[0];
                return !dateAvailability[dateKey] && !isPastDate(d) && d <= nextWeek;
            });

            // Check first 7 days in parallel
            Promise.all(
                datesToCheck.slice(0, 7).map(async (date) => {
                    try {
                        const slots = await getAvailableSlots(date);
                        const dateKey = date.toISOString().split('T')[0];
                        setDateAvailability(prev => ({
                            ...prev,
                            [dateKey]: slots.length > 0
                        }));
                    } catch (error) {
                        // Silently fail for preloading
                    }
                })
            );
        };

        preloadAvailability();
    }, []);

    const handleDateSelect = async (date) => {
        if (loadingSlots || isPastDate(date)) return;

        setSelectedDate(date);
        setStep(2);
        setLoadingSlots(true);
        setAvailableTimeSlots([]);
        setSelectedTime(null);

        announceToScreenReader(`Loading available times for ${formatDisplayDate(date)}`, 'polite');

        try {
            const slots = await getAvailableSlots(date);
            const sortedSlots = sortTimeSlots(slots);

            if (sortedSlots.length === 0) {
                showToast('No available time slots for this date. Please select another date.', 'warning');
                announceToScreenReader('No available time slots for this date', 'assertive');
            } else {
                announceToScreenReader(`${sortedSlots.length} time slots available`, 'polite');
            }

            setAvailableTimeSlots(sortedSlots);
        } catch (error) {
            console.error('Error loading slots:', error);
            showToast('Error loading available times. Please try again.', 'error');
            announceToScreenReader('Error loading available times', 'assertive');
            setAvailableTimeSlots(sortTimeSlots(["10:00 AM", "11:30 AM", "02:00 PM", "04:30 PM", "06:00 PM"]));
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleTimeSelect = async (time) => {
        if (loadingSlots) return;

        setLoadingSlots(true);
        try {
            if (selectedDate) {
                const isAvailable = await checkAvailability(selectedDate, time);
                if (!isAvailable) {
                    showToast('This time slot is no longer available. Please select another time.', 'error');
                    announceToScreenReader('Time slot no longer available', 'assertive');
                    const slots = await getAvailableSlots(selectedDate);
                    setAvailableTimeSlots(sortTimeSlots(slots));
                    return;
                }
            }
            setSelectedTime(time);
            // Notify parent
            onSlotSelect?.(selectedDate, time);
            announceToScreenReader(`Selected ${time}.`, 'polite');
        } catch (error) {
            showToast('Error checking availability. Please try again.', 'error');
            announceToScreenReader('Error checking availability', 'assertive');
        } finally {
            setLoadingSlots(false);
        }
    };

    const goBack = () => {
        if (step === 2) {
            setStep(1);
            setSelectedDate(null);
            setSelectedTime(null);
        } else {
            onCancel?.();
        }
    };

    return (
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-200 overflow-hidden max-w-lg w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#fdfbf7] to-[#fae1e5] px-4 sm:px-6 py-4 border-b border-[#eaddcf] flex justify-between items-center">
                <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-serif text-[#c5a059] flex items-center">
                        <Calendar className="mr-2 flex-shrink-0" size={18} />
                        <span className="truncate">{title || 'Select Date & Time'}</span>
                    </h3>
                    {subtitle && (
                        <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
                    )}
                    <div className="flex items-center mt-1">
                        <p className="text-xs text-gray-500">Step {step} of 2</p>
                        <div className="ml-3 flex gap-1">
                            {[1, 2].map((s) => (
                                <div
                                    key={s}
                                    className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${s === step ? 'bg-[#c5a059] w-6' : s < step ? 'bg-[#d48c95]' : 'bg-gray-300'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <button
                    onClick={goBack}
                    className="text-gray-400 hover:text-[#d48c95] flex items-center text-sm transition-colors duration-200 ml-2 min-h-[44px] min-w-[44px] justify-center"
                    aria-label={step === 1 ? 'Cancel' : 'Go back to date selection'}
                >
                    <ArrowLeft size={16} className="mr-1" />
                    <span className="hidden sm:inline">{step === 1 ? 'Cancel' : 'Back'}</span>
                </button>
            </div>

            <div className="p-4 sm:p-6">
                {/* STEP 1: DATE SELECTION */}
                {step === 1 && (
                    <div className="animate-fadeIn">
                        <h4 className="text-base font-medium text-gray-800 mb-4 flex items-center">
                            <Calendar className="mr-2 text-[#d48c95] flex-shrink-0" size={18} />
                            <span>Select a Date</span>
                        </h4>
                        <div className="grid grid-cols-5 sm:grid-cols-7 gap-1.5 sm:gap-2">
                            {availableDates.map((date, i) => {
                                const dateKey = date.toISOString().split('T')[0];
                                const hasSlots = dateAvailability[dateKey] !== false;
                                const dateIsToday = isToday(date);
                                const isPast = isPastDate(date);
                                const isHovered = hoveredDate === dateKey;

                                return (
                                    <button
                                        key={i}
                                        onClick={() => handleDateSelect(date)}
                                        onMouseEnter={() => setHoveredDate(dateKey)}
                                        onMouseLeave={() => setHoveredDate(null)}
                                        onKeyDown={handleKeyboardNavigation(
                                            () => handleDateSelect(date),
                                            null, null, null
                                        )}
                                        disabled={loadingSlots || isPast || hasSlots === false}
                                        className={`flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg border-2 transition-all duration-300 group relative min-h-[56px] sm:min-h-[64px] ${hasSlots === false || isPast
                                            ? 'border-gray-200 opacity-40 cursor-not-allowed'
                                            : 'border-gray-100 hover:border-[#c5a059] hover:bg-gradient-to-br hover:from-[#fdfbf7] hover:to-[#fae1e5] cursor-pointer shadow-sm hover:shadow-md'
                                            } ${dateIsToday ? 'ring-2 ring-[#c5a059] ring-opacity-40 bg-[#fdfbf7]' : ''} ${isHovered && !isPast && hasSlots !== false ? 'scale-105' : ''
                                            }`}
                                        title={hasSlots === false ? 'No available slots' : isPast ? 'Past date' : `Select ${formatDisplayDate(date)}`}
                                        aria-label={hasSlots === false ? 'No available slots' : isPast ? 'Past date' : `Select ${formatDisplayDate(date)}`}
                                    >
                                        {dateIsToday && (
                                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#c5a059] rounded-full animate-pulse"></span>
                                        )}
                                        <span className={`text-[10px] sm:text-xs uppercase font-bold transition-colors ${hasSlots === false || isPast ? 'text-gray-300' : 'text-gray-500 group-hover:text-[#c5a059]'
                                            }`}>
                                            {date.toLocaleDateString('en-US', { weekday: 'short' })}
                                        </span>
                                        <span className={`text-base sm:text-lg font-serif font-semibold transition-colors ${hasSlots === false || isPast ? 'text-gray-300' : 'text-gray-800 group-hover:text-[#c5a059]'
                                            }`}>
                                            {date.getDate()}
                                        </span>
                                        {hasSlots === false && !isPast && (
                                            <span className="absolute inset-0 flex items-center justify-center">
                                                <span className="text-xs text-gray-400">—</span>
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* STEP 2: TIME SELECTION */}
                {step === 2 && (
                    <div className="animate-fadeIn">
                        <h4 className="text-base font-medium text-gray-800 mb-2 flex items-center">
                            <Clock className="mr-2 text-[#d48c95] flex-shrink-0" size={18} />
                            <span>Select a Time</span>
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-500 mb-4">
                            Available slots for {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </p>
                        {loadingSlots ? (
                            <div className="text-center py-12">
                                <Loader2 className="w-8 h-8 text-[#c5a059] animate-spin mx-auto mb-3" />
                                <p className="text-gray-500">Loading available times...</p>
                            </div>
                        ) : availableTimeSlots.length === 0 ? (
                            <div className="text-center py-12">
                                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 mb-2 font-medium">No available time slots for this date.</p>
                                <p className="text-sm text-gray-400 mb-6">Please select a different date.</p>
                                <button
                                    onClick={() => {
                                        setStep(1);
                                        setSelectedDate(null);
                                    }}
                                    className="px-6 py-2 bg-[#c5a059] text-white rounded-full hover:bg-[#b08e4d] transition text-sm font-medium"
                                >
                                    Select a Different Date
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                                {availableTimeSlots.map((time, i) => (
                                    <button
                                        key={i}
                                        onClick={() => handleTimeSelect(time)}
                                        onKeyDown={handleKeyboardNavigation(
                                            () => handleTimeSelect(time),
                                            null, null, null
                                        )}
                                        disabled={loadingSlots}
                                        className="py-3 px-3 sm:px-4 rounded-full border-2 border-gray-200 text-gray-600 hover:bg-gradient-to-r hover:from-[#d48c95] hover:to-[#c07b83] hover:text-white hover:border-[#d48c95] transition-all duration-300 text-xs sm:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#c5a059] focus:ring-offset-2 shadow-sm hover:shadow-md min-h-[48px]"
                                        aria-label={`Select ${time}`}
                                    >
                                        <span className="flex items-center justify-center">
                                            <Clock size={12} className="mr-1.5 opacity-70 flex-shrink-0" />
                                            <span className="truncate">{time}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default BookingCalendar;
